import * as React from 'react'
import { MutableRefObject, PropsWithChildren, ReactElement, useEffect, useMemo, useState } from 'react'
import { Callout, IconButton, Image, ImageLoadState, MessageBar, MessageBarType, Stack, StackItem, Text, TextField } from '@fluentui/react'
import { useBoolean } from '@fluentui/react-hooks'
import Loading from '../../components/Loading'
import DocumentName from '../Render/DocumentName'
import { stealthClick } from '../StealthLink'
import useAxios from '../../hooks/useAxios'
import { Destination } from '../../context/AxiosContext'
import { DocumentNodeType } from '../../config/documentConfig'
import { getDocumentExtension, getDocumentNameFromPath, getExtensionChange, previewFileTypes } from '../../helpers/documentHelper'
import { DocumentOpenType } from '../../context/GlobalContext'
import { useSettings } from '../../hooks/useSettings'
import { formatInputDocumentName } from '../../helpers/regexHelper'
import { useTranslation } from 'react-i18next'

type Item = {
    id: string
    name: string
    type?: DocumentNodeType
}

type Props = PropsWithChildren<{
    item: Item
    targetRef: MutableRefObject<HTMLDivElement>
    visible: boolean
    hide: () => void
    reset: () => void // Callback for when the view needs to be updated.
}>

export default function DocumentPreview(props: Props): ReactElement {
    const axios = useAxios()
    const { settings } = useSettings()
    const { t: translate } = useTranslation()

    const [loaded, { setFalse: setUnloaded, setTrue: setLoaded }] = useBoolean(false)
    const [preview, setPreview] = useState<string>()
    const isValid = useMemo(() => previewFileTypes.includes(getDocumentExtension(props.item.name)), [props.item.name])
    const [isRenameMode, { setTrue: startRenameMode, setFalse: stopRenameMode }] = useBoolean(false)
    const [filename, setFilename] = useState<string>('')
    const [renameError, setRenameError] = useState<string>()

    const extensionChange = useMemo(() => getExtensionChange(props.item.name, filename), [props.item.name, filename])

    const startRename = () => {
        setFilename(props.item.name)
        setRenameError(undefined)
        startRenameMode()
    }

    const renameDocument = () => {
        const name = formatInputDocumentName(filename || '')
        if (!name) {
            setRenameError('Er is geen bestandsnaam ingevuld')
            return
        }
        if (!getDocumentExtension(name)) {
            setRenameError(translate('file.pivot.document.mutate.form.name.extension.required'))
            return
        }
        setRenameError(undefined)
        axios
            .request({
                method: 'patch',
                url: `/items/${props.item.id}`,
                baseURL: Destination.Documents,
                headers: { SuppressErrors: 'true' },
                data: { name },
                params: { '@microsoft.graph.conflictBehavior': 'rename' },
            })
            .then(() => props.reset())
            .then(hideCallout)
            .catch(() => setRenameError('Hernoemen is mislukt. Probeer het opnieuw.'))
    }

    const hideCallout = () => {
        stopRenameMode()
        props.hide()
    }

    useEffect(() => {
        if (props.visible && !preview && isValid) {
            axios
                .get(`/items/${props.item.id}/thumbnails/0/large`, {
                    baseURL: Destination.Documents,
                })
                .then(response => setPreview(response.data.url))
        }
    }, [props.visible])

    if (!isValid) {
        return <></>
    }

    return (
        <>
            {props.children}
            <Callout
                target={props.targetRef.current}
                calloutMaxWidth={window.innerWidth - 32}
                calloutMaxHeight={window.innerHeight - 32}
                coverTarget={false}
                gapSpace={8}
                isBeakVisible={true}
                onDismiss={hideCallout}
                hidden={!props.visible}
            >
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 's2', padding: 's1' }}>
                    <Text variant="large" block nowrap styles={{ root: { flexGrow: 1, minWidth: 0 } }}>
                        {isRenameMode ? (
                            <TextField
                                autoFocus
                                value={filename}
                                errorMessage={renameError}
                                onChange={(_, value?: string) => {
                                    setFilename(value)
                                    setRenameError(undefined)
                                }}
                                // Pre-select only the base name, so typing straight away keeps the extension.
                                // Uses the event target instead of a componentRef: on the initial autofocus
                                // the ref is not attached yet, so a ref-based select silently does nothing.
                                onFocus={e => {
                                    if (filename) {
                                        e.currentTarget.setSelectionRange(0, getDocumentNameFromPath(filename).length)
                                    }
                                }}
                                onKeyDown={e => e.key === 'Enter' && renameDocument()}
                            />
                        ) : (
                            <DocumentName
                                id={props.item.id}
                                isLink={false}
                                name={props.item.name}
                                nodeType={props.item.type || DocumentNodeType.Document}
                                iconSize={20}
                                iconValueProps={{ textVariant: 'mediumPlus' }}
                            />
                        )}
                    </Text>
                    <StackItem disableShrink>
                        {isRenameMode ? (
                            <>
                                <IconButton title="Opslaan en sluiten" iconProps={{ iconName: 'SaveAndClose' }} onClick={renameDocument} />
                                <IconButton title="Annuleren" iconProps={{ iconName: 'Cancel' }} onClick={() => stopRenameMode()} />{' '}
                            </>
                        ) : (
                            <>
                                <IconButton title="Hernoemen" iconProps={{ iconName: 'Rename' }} onClick={startRename} />
                                <IconButton
                                    title="Bestand openen"
                                    iconProps={{ iconName: 'Download' }}
                                    onClick={() =>
                                        stealthClick({ id: props.item.id, documentsOpen: settings.documentsOpen ?? DocumentOpenType.Open }, axios) &&
                                        hideCallout()
                                    }
                                />
                                <IconButton title="Sluiten" iconProps={{ iconName: 'Cancel' }} onClick={hideCallout} />
                            </>
                        )}
                    </StackItem>
                </Stack>
                {isRenameMode && extensionChange.isChanged && (
                    <MessageBar messageBarType={MessageBarType.warning}>
                        {translate('file.pivot.document.mutate.form.name.extension.changed', {
                            oldExtension: extensionChange.oldExtension,
                            newExtension: extensionChange.newExtension,
                        })}
                    </MessageBar>
                )}
                <StackItem>
                    {!loaded && <Loading />}
                    {preview && (
                        <Image
                            src={preview}
                            styles={{ image: { maxHeight: '100%', maxWidth: '100%' } }}
                            onLoadingStateChange={loadState => (loadState === ImageLoadState.notLoaded ? setUnloaded() : setLoaded())}
                        />
                    )}
                </StackItem>
            </Callout>
        </>
    )
}
